import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ApplicationSignup.css';

const resolveApiBaseUrl = () => {
  const envBase = process.env.REACT_APP_API_BASE_URL;
  if (envBase && envBase.trim()) return envBase.trim().replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]') {
      return 'http://127.0.0.1:8000/api';
    }
    return `http://${host}:8000/api`;
  }
  return 'http://127.0.0.1:8000/api';
};

const API_BASE_URL = resolveApiBaseUrl();
const apiUrl = (path = '') => `${API_BASE_URL}/${String(path).replace(/^\/+/, '')}`;
const WORKER_FIELDS = [
  ['username', 'Username'],
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['email', 'Email'],
  ['phone_number', 'Phone number'],
  ['dob', 'DOB'],
  ['zip_code', 'ZIP code'],
  ['professional_email', 'Professional email'],
];

const SignUpWorker = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '', first_name: '', last_name: '', email: '', phone_number: '', dob: '', zip_code: '',
    password: '', confirm_password: '', requested_company_id: '', professional_email: '',
    years_experience: '', specialties: '', portfolio_url: '', message: '',
  });

  useEffect(() => {
    axios.get(apiUrl('userprofiles/worker-applications/companies/'))
      .then((response) => {
        const rows = Array.isArray(response.data) ? response.data : [];
        setCompanies(rows);
        if (rows.length) {
          setFormData((prev) => ({ ...prev, requested_company_id: String(rows[0].id) }));
        }
      })
      .catch(() => setCompanies([]));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      await axios.post(apiUrl('register-worker-application/'), {
        ...formData,
        requested_company_id: Number(formData.requested_company_id),
        years_experience: formData.years_experience === '' ? null : Number(formData.years_experience),
      });
      setMessage('Worker application submitted. You can log in after approval.');
      setTimeout(() => navigate('/login'), 900);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Unable to submit worker application.');
    }
  };

  return (
    <div className="application-shell">
      <div className="application-card">
        <header className="application-header">
          <h1>Worker Application</h1>
          <p>
            Apply directly to an owner&apos;s company. Your application stays pending until reviewed by the owner
            or an admin.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="application-form">
          <div className="application-grid">
            {WORKER_FIELDS.map(([field, label]) => (
              <div key={field} className="field-group">
                <label htmlFor={field}>{label}:</label>
                <input
                  id={field}
                  type={field === 'dob' ? 'date' : field.includes('email') ? 'email' : 'text'}
                  value={formData[field]}
                  onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
                  required
                />
              </div>
            ))}
            <div className="field-group field-wide">
              <label htmlFor="requested_company_id">Select owner / company:</label>
              <select
                id="requested_company_id"
                value={formData.requested_company_id}
                onChange={(e) => setFormData((prev) => ({ ...prev, requested_company_id: e.target.value }))}
                required
              >
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="password-row">
            <div className="field-group">
              <label htmlFor="password">Password:</label>
              <div className="password-input-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="field-group">
              <label htmlFor="confirm_password">Confirm password:</label>
              <input
                id="confirm_password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => setFormData((prev) => ({ ...prev, confirm_password: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="application-grid application-section-spacer">
            <div className="field-group">
              <label htmlFor="years_experience">Years experience:</label>
              <input
                id="years_experience"
                type="number"
                value={formData.years_experience}
                onChange={(e) => setFormData((prev) => ({ ...prev, years_experience: e.target.value }))}
              />
            </div>
            <div className="field-group">
              <label htmlFor="portfolio_url">Portfolio URL:</label>
              <input
                id="portfolio_url"
                type="url"
                value={formData.portfolio_url}
                onChange={(e) => setFormData((prev) => ({ ...prev, portfolio_url: e.target.value }))}
              />
            </div>
            <div className="field-group field-wide">
              <label htmlFor="specialties">Specialties:</label>
              <input
                id="specialties"
                type="text"
                value={formData.specialties}
                onChange={(e) => setFormData((prev) => ({ ...prev, specialties: e.target.value }))}
                placeholder="Braids, loc maintenance, color, barbering..."
              />
            </div>
            <div className="field-group field-wide">
              <label htmlFor="message">Message:</label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                placeholder="Tell the owner about your background and what you bring to the team."
              />
            </div>
          </div>

          <div className="application-footer">
            <button type="submit" className="application-submit">Submit Worker Application</button>
            <Link to="/signup-owner" className="application-switch-link">Apply as business owner instead</Link>
          </div>
          {message ? <p className="application-message">{message}</p> : null}
        </form>
      </div>
    </div>
  );
};

export default SignUpWorker;
